/* jshint esversion: 6 */
import React, { useEffect } from 'react';
import * as _ from 'underscore';
import { SUB_PREVIEW_UPDATE } from 'app/redux/constants.js';
import DowngradeBlockedDialog from '@/shadcn/components/account/PlanChange/DowngradeBlockedDialog.tsx';
import PlanChangeDialog from '@/shadcn/components/account/PlanChange/PlanChangeDialog.tsx';
import { number } from '@/shadcn/components/account/PlanChange/format.ts';
import { listPrice } from '@/shadcn/components/account/PlanChange/plans.ts';
import useChangePreview, { useConfirmationStep } from './useChangePreview.js';

/*
 * What the smaller plan can't keep running, from SUB_LIMIT_CHECK, one sentence each.
 * The three feature-only blockers are what Plus and Agency+ lose on the way down;
 * on their own they don't need everything switched off.
 */
const FEATURE_ONLY = ['everqueue', 'bulkImport', 'categories'];

function downgradeBlockers(props, check, label) {
    const perms = props.user.perms,
        blockers = [];

    if (check.outputs < 0) {
        let inUse = parseInt(perms.perms.hardLimitOutputs, 10) - parseInt(perms.resources.outputsAvailable, 10);
        blockers.push({ key: 'outputs', text: `${number(inUse)} destinations are on. ${label} includes ${number(check.totalOutputs)}.` });
    }
    if (check.inputs < 0) {
        let inUse = parseInt(perms.perms.hardLimitInputs, 10) - parseInt(perms.resources.inputsAvailable, 10);
        blockers.push({ key: 'inputs', text: `${number(inUse)} sources are on. ${label} includes ${number(check.totalInputs)}.` });
    }
    if (props.isPlusUser() || props.isAgencyPlusUser()) {
        if (!check.everqueue) {
            blockers.push({ key: 'everqueue', text: 'EverQueue content will stop posting.' });
        }
        if (!check.bulkImport) {
            blockers.push({ key: 'bulkImport', text: "Content search and import won't be available." });
        }
        if (!check.categories) {
            blockers.push({ key: 'categories', text: 'Category schedules will stop posting.' });
        }
    }
    if (check.multiAvailable === 0 && check.multiSafe === false) {
        blockers.push({ key: 'multi', text: `${label} allows one account per network.` });
    }
    if (check.twitterInputEnabled === 0 && parseInt(check.twitterInput, 10) > 0) {
        blockers.push({ key: 'twitterInput', text: `X sources aren't available on ${label}.` });
    }
    if (check.usersAvailable < check.userCount) {
        blockers.push({ key: 'extraSeats', text: `${number(check.userCount)} team members are set up. ${label} includes ${number(check.usersAvailable)}.` });
    }
    if (!check.hashStatic && check.hashStaticUsed) {
        blockers.push({ key: 'hashStatic', text: 'Fixed hashtags will stop posting.' });
    }
    if (!check.hashPool && check.hashPoolUsed) {
        blockers.push({ key: 'hashPool', text: 'Dynamic hashtags will turn off.' });
    }
    if (!check.autoHash && check.autoHashUsed) {
        blockers.push({ key: 'autoHash', text: 'Auto-detected hashtags will turn off.' });
    }
    if (!check.feedPush && check.feedPushUsed) {
        blockers.push({ key: 'feedPush', text: 'Real-time source updates will stop.' });
    }

    return blockers;
}

/*
 * Opens through the legacy modal stack to confirm a move to a smaller paid plan. Moving
 * to the free plan goes through the Cancel account flow
 * instead. Subscription.updatePlan passes the target `newPlanLevel` / `newPlanTerm`, the
 * social packs that carry over (`adjustedAddOns`), the target's limits (`newLimits`) and
 * `planChangeHandler`. The page closes the modal once SUB_UPDATE settles.
 *
 * Downgrades take effect at once, so if current usage won't fit the smaller plan it says
 * what's over and offers to go and choose what stays on, or to turn everything off now.
 */
export default function DowngradeConfirm(props) {
    const { app, plans, user, adjustedAddOns, newLimits } = props,
        constants = app.config.constants,
        level = parseInt(props.newPlanLevel, 10),
        term = parseInt(props.newPlanTerm, 10),
        current = plans.current,
        fromDetail = plans.details[parseInt(current.planLevel, 10)] || {},
        toDetail = plans.details[level] || {},
        check = plans.limitCheck,
        trial = current.trialEndsAt > 0 && current.trialEndsAt * 1000 > Date.now(),
        post = { level, term };

    if (adjustedAddOns) {
        post.add_on_social = adjustedAddOns;
    }

    const preview = useChangePreview(props, SUB_PREVIEW_UPDATE, post);

    useConfirmationStep(true, 'Downgrade');

    useEffect(() => {
        props.limitCheck(level, adjustedAddOns || 0);
    }, []);

    if (_.isEmpty(check) || parseInt(check.level, 10) !== level) {
        return null;
    }

    const blockers = downgradeBlockers(props, check, toDetail.label),
        busy = app.loading.SUB_UPDATE,
        close = () => props.closeAllModals();

    if (blockers.length) {
        return (
            <DowngradeBlockedDialog
                label={toDetail.label}
                blockers={blockers.map(({ text }) => text)}
                busy={busy}
                onChooseWhatToKeep={() => {
                    props.router.navigate('/share/automate');
                    props.closeAllModals();
                }}
                onTurnOffAll={() => {
                    props.planChangeHandler({
                        level,
                        term,
                        isHammertime: !blockers.every(({ key }) => FEATURE_ONLY.includes(key)),
                        trackingAction: 'downgrade'
                    });
                }}
                onClose={close} />
        );
    }

    const currentLimits = props.calculateAddOns(),
        packs = {
            add_on_social: parseInt(current.addOns?.add_on_social?.qty || 0, 10),
            add_on_posts_1: parseInt(current.addOns?.add_on_posts_1?.qty || 0, 10)
        },
        // a trial pays nothing now, so compare what each plan costs once it ends
        afterTrial = (detail, socialPacks) => listPrice(detail, constants.SUBSCRIPTION_TERM_MONTHLY, { ...packs, add_on_social: socialPacks });

    return (
        <PlanChangeDialog
            kind={trial ? 'downgradeTrial' : 'downgrade'}
            from={{
                label: fromDetail.label,
                outputs: currentLimits.outputs,
                inputs: currentLimits.inputs,
                postLimit: parseInt(user.perms.perms.postLimit, 10) || 0,
                price: trial ? afterTrial(fromDetail, packs.add_on_social) : Number(currentLimits.price)
            }}
            to={{
                label: toDetail.label,
                outputs: newLimits.outputs,
                inputs: newLimits.inputs,
                postLimit: parseInt(toDetail.limits?.postLimit, 10) || 0,
                price: trial ? afterTrial(toDetail, adjustedAddOns || 0) : Number(newLimits.price)
            }}
            annual={term === constants.SUBSCRIPTION_TERM_ANNUAL}
            trialEnds={trial ? current.trialEndsAt * 1000 : null}
            nextBilling={current.nextBilling}
            preview={preview}
            busy={busy}
            onConfirm={() => {
                let data = {
                    level,
                    term,
                    trackingAction: 'downgrade'
                };
                if (adjustedAddOns) {
                    data.add_on_social = adjustedAddOns;
                }
                props.planChangeHandler(data);
            }}
            onCancel={close}
            onClose={close} />
    );
}
